import { AiCard, AiLoadingPanel, Button, CenteredLayout, Css, Tag, TagType, Tokens, useTestIds } from "@homebound/beam";
import { createMaterialDetailsUrl } from "src/RouteUrls";
import type { MaterialImportAiProposal_ReviewFragment } from "src/routes/libraries/material-catalog/material-import/MaterialImportPage.generated";
import { MATERIAL_PHOTO_EMPTY_STATE_IMG_URL } from "src/utils/constants";

type DuplicateCandidate = MaterialImportAiProposal_ReviewFragment["duplicateCandidates"][number];

export type MaterialImportCandidatesStepProps = {
  /** Best match first (backend order). */
  candidates: DuplicateCandidate[];
  /** True while the user's pick is being imported. */
  resolving: boolean;
  /** Imports into the given listing, or creates a new one when omitted. */
  onResolve: (materialListingId?: string) => void;
};

/**
 * The wizard's second step while the import is waiting on the user to select a potential duplicate listing to import into.
 */
export function MaterialImportCandidatesStep(props: MaterialImportCandidatesStepProps) {
  const { candidates, resolving, onResolve } = props;
  const tid = useTestIds(props, "materialImportCandidates");
  const single = candidates.length === 1;

  if (resolving) {
    return (
      <CenteredLayout size="sm">
        <AiLoadingPanel title="We're importing your material..." omitBg />
      </CenteredLayout>
    );
  }

  return (
    <CenteredLayout size="sm">
      <AiCard size="lg">
        <div css={Css.df.fdc.gap3.px3.py2.$}>
          <div css={Css.df.fdc.gap1.$}>
            <span css={Css.lg.aiBoldText.$} {...tid.title}>
              {single ? "We Found a Similar Material" : "We Found Possible Matches"}
            </span>
            <span css={Css.sm.color(Tokens.OnSurface).$} {...tid.description}>
              {single
                ? "A similar material already exists in your catalog. Select it to update its listing, or continue creating a new one."
                : "Similar materials already exist in your catalog. Select an existing material to update its listing, or continue creating a new one."}
            </span>
          </div>
          <div css={Css.df.fdc.gap2.$}>
            {candidates.map((candidate, i) => (
              <CandidateRow
                key={candidate.id}
                candidate={candidate}
                tag={candidateTag(i, candidates.length, candidate.score)}
                onUse={() => onResolve(candidate.materialListing.id)}
                {...tid.candidate}
              />
            ))}
          </div>
          <div css={Css.df.aic.jcc.gap1.pt1.$} {...tid.footer}>
            <span css={Css.sm.color(Tokens.OnSurface).$}>
              {single ? "This doesn't look right?" : "None of these look right?"}
            </span>
            <Button label="Continue as new material" variant="text" onClick={() => onResolve()} />
          </div>
        </div>
      </AiCard>
    </CenteredLayout>
  );
}

type CandidateRowProps = { candidate: DuplicateCandidate; tag: { text: string; type: TagType }; onUse: VoidFunction };

function CandidateRow(props: CandidateRowProps) {
  const { candidate, tag, onUse } = props;
  const tid = useTestIds(props);
  const { id, name, brand, defaultVariant } = candidate.materialListing;

  return (
    <div css={Css.df.aic.gap3.p2.br12.bgWhite.$} {...tid}>
      <img
        css={Css.wPx(120).hPx(120).objectCover.br8.ba.bcGray200.$}
        src={defaultVariant?.featuredImage?.asset?.previewUrl || MATERIAL_PHOTO_EMPTY_STATE_IMG_URL}
        alt={name ?? ""}
        {...tid.image}
      />
      <div css={Css.df.fdc.aifs.gapPx(2).fg1.mw0.$}>
        <div {...tid.tag}>
          <Tag text={tag.text} type={tag.type} />
        </div>
        <span css={Css.smSb.$} {...tid.brand}>
          {brand?.name}
        </span>
        <span css={Css.sm.color(Tokens.OnSurface).$} {...tid.name}>
          {name}
        </span>
      </div>
      <div css={Css.df.fdc.aic.gap1.$}>
        <Button label="Use this Material" variant="secondary" onClick={onUse} />
        <a
          href={createMaterialDetailsUrl(id)}
          target="_blank"
          rel="noreferrer"
          css={Css.xsSb.blue600.tdn.$}
          {...tid.viewMaterial}
        >
          View Material
        </a>
      </div>
    </div>
  );
}

/** The 'score' of a potential duplicate. Calling out the best match for extra emphasis. */
function candidateTag(index: number, count: number, score: number): { text: string; type: TagType } {
  const label = count > 1 && index === 0 ? "Best Match" : "Possible Match";
  return {
    text: `${label} • ${Math.round(score * 100)}% Similar`.toUpperCase(),
    type: index === 0 ? "success" : "info",
  };
}
